/**
 * Formats the backend defines itself rather than reading from Scryfall
 * legalities. Classic Legacy is a positive-only predicate: there is no
 * "not legal in" side, so the Legality control is hidden for it and
 * `formatLegality` never reaches the URL or the wire.
 */
export const CLASSIC_LEGACY_FORMAT = 'classic_legacy';

/**
 * Display labels for format wire values that should not render raw. Lowercase,
 * like every other format in the list (those render their wire value as is).
 */
export const FORMAT_LABELS: Record<string, string> = {
  [CLASSIC_LEGACY_FORMAT]: 'classic legacy',
};

/** Hover text for format options that need explaining. */
export const FORMAT_TITLES: Record<string, string> = {
  [CLASSIC_LEGACY_FORMAT]:
    'Unofficial format: Alpha through Rise of the Eldrazi, reprints allowed. classiclegacymtg.com',
};

export const formatLabel = (format: string): string => FORMAT_LABELS[format] ?? format;

/** False for formats whose legality cannot be inverted. */
export const formatHasLegality = (format: string): boolean =>
  format.toLowerCase() !== CLASSIC_LEGACY_FORMAT;

/**
 * Format landing pages (Asana 1219287567650303): the eight keys, mapped to the
 * Scryfall legality keys of the same name on the backend.
 */
export const FORMAT_PAGE_KEYS = [
  'standard',
  'pioneer',
  'modern',
  'legacy',
  'vintage',
  'pauper',
  'commander',
  'premodern',
] as const;
export type FormatPageKey = (typeof FORMAT_PAGE_KEYS)[number];

export const FORMAT_PAGE_LABELS: Record<FormatPageKey, string> = {
  standard: 'Standard',
  pioneer: 'Pioneer',
  modern: 'Modern',
  legacy: 'Legacy',
  vintage: 'Vintage',
  pauper: 'Pauper',
  commander: 'Commander',
  premodern: 'Premodern',
};

/** Ticket copy, verbatim. */
export const FORMAT_INTRO_COPY: Record<FormatPageKey, string> = {
  standard: 'Standard uses the most recent Standard-legal sets and rotates as new sets release.',
  pioneer: 'Pioneer is a non-rotating format that allows cards from Return to Ravnica forward.',
  modern: 'Modern is a non-rotating format that allows cards from Eighth Edition forward.',
  legacy: "Legacy allows cards from all of Magic's history, apart from its banned list.",
  vintage: 'Vintage allows nearly every card ever printed, with a restricted list that limits the strongest cards to one copy.',
  pauper: 'Pauper allows only cards that have been printed at common rarity.',
  commander: 'Commander is a 100-card singleton format built around a legendary creature.',
  premodern: 'Premodern is a player-run format built on cards from Fourth Edition through Scourge.',
};

/** Follows every format intro. */
export const FORMAT_INTRO_SUFFIX = 'These lists come from live TradeMagic trader data and refresh every night.';

/** /formats index tag pills (Asana 1219902842494504). */
export const FORMAT_PAGE_TAGS: Record<FormatPageKey, string> = {
  standard: 'Rotating',
  pioneer: 'Eternal',
  modern: 'Eternal',
  legacy: 'Eternal',
  vintage: 'Eternal',
  pauper: 'Eternal',
  commander: 'Casual',
  premodern: 'Eternal',
};

/** /formats index one-line copy, verbatim from the ticket. */
export const FORMAT_SHORT_COPY: Record<FormatPageKey, string> = {
  standard: 'The most recent Standard-legal sets; rotates as new sets release.',
  pioneer: 'Non-rotating. Return to Ravnica forward.',
  modern: 'Non-rotating. Eighth Edition forward.',
  legacy: "All of Magic's history, minus its banned list.",
  vintage: 'Nearly every card ever printed; a restricted list caps the strongest.',
  pauper: 'Only cards printed at common rarity.',
  commander: '100-card singleton built around a legendary creature.',
  premodern: 'Player-run. Fourth Edition through Scourge.',
};
